import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DateUtils,
  GetStylesFunction,
  IconHeaderWithSearch,
  LoadingSpinner,
  enumContainer,
  mixins,
  omitNullsy,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {
  LightMonthRangeFilter,
  LightOrganizationFilter2,
  LightFilterPanel,
  LightSelectFilter,
  LightSelectedFiltersPane,
  TablePaging,
  TableSorting,
  UserFilterDto,
  WallabyCoreSchema,
  useAgens,
  useFormatters,
  useOrganizationsByOrgSecurity,
  useTableData,
  useTableParams,
  useUserFilters,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';
import {Helmet} from 'react-helmet';

import {HaiByPathogensData, HaiByPathogensReportTablesAndChart} from './HaiByPathogensReportTablesAndChart';
import {useAppSelector} from 'src/js/store_hooks';

type PathogensReportFilters = {
  dateFrom: string;
  dateTo: string;
  floatingShortcutName: ShortcutEnum | null;
  haiState: string;
  haiTypes: string[] | null;
  organizationIds: string[] | null;
  pathogenIds: string[] | null;
};
type FiltersData = {
  reportOpen: boolean;
  report: PathogensReportFilters;
  haisByPathogenSort: TableSorting;
  haisSort: TableSorting;
  haisPaging: TablePaging;
};
const DEFAULT_DEPARTMENT_REPORT_FILTERS_OPEN = true;
const DEFAULT_DEPARTMENT_REPORT_FILTERS = (_organizations: WallabyCoreSchema.components['schemas']['OrganizationFilterDto'][]) => {
  const [startOfLastMonth, endOfLastMonth] = DateUtils.lastCompletedMonth();
  return {
    dateFrom: startOfLastMonth,
    dateTo: endOfLastMonth,
    floatingShortcutName: ShortcutEnum.PREVIOUS_MONTH,
    haiState: 'ALL',
    haiTypes: null,
    organizationIds: null,
    pathogenIds: null,
  } as PathogensReportFilters;
};
const DEFAULT_HAIS_BY_PATHOGEN_SORTING: TableSorting = {
  field: 'total',
  direction: 'desc',
};
const DEFAULT_HAIS_SORTING: TableSorting = {};
interface Props {
  config: ConfigObjectType;
}
const DEFAULT_HAIS_PAGING: TablePaging = {};
export function normalizeHaiByPathogensFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.floatingShortcutName) {
    delete (copy as any).dateFrom;
    delete (copy as any).dateTo;
  }
  return copy;
}
export const HaiByPathogensReportLayoutContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'haiByPathogens';
  const storageIdPrefix = '/haiByPathogens';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const defaultReportFilters = DEFAULT_DEPARTMENT_REPORT_FILTERS(organizations);
  const filters: FiltersData = useMemo(() => {
    return {
      reportOpen: query?.reportOpen ?? DEFAULT_DEPARTMENT_REPORT_FILTERS_OPEN,
      report: {
        ...defaultReportFilters,
        ...(query?.report ?? {}),
      },
      haisByPathogenSort: query?.haisByPathogenSort ?? DEFAULT_HAIS_BY_PATHOGEN_SORTING,
      haisSort: query?.haisSort ?? DEFAULT_HAIS_SORTING,
      haisPaging: query?.haisPaging ?? DEFAULT_HAIS_PAGING,
    };
  }, [query, defaultReportFilters]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({report: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <HaiByPathogensReportLayoutContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
HaiByPathogensReportLayoutContainer.displayName = 'HaiByPathogensReportLayoutContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};
const HaiByPathogensReportLayoutContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const {data: organizations} = useOrganizationsByOrgSecurity(config);
  const haveDefaultFilters = organizations.length > 0;
  const defaultReportFilters = DEFAULT_DEPARTMENT_REPORT_FILTERS(organizations);
  // get data
  const pageTitle = 'navigation.route.haiByPathogens';
  const reportUrl = config.service.url.pathogenReport;
  const reportParams = useTableParams(
    {
      dateFrom: filters.report.dateFrom,
      dateTo: filters.report.dateTo,
      floatingShortcutName: filters.report.floatingShortcutName,
      haiState: filters.report.haiState,
      haiTypes: filters.report.haiTypes,
      organizationIds: filters.report.organizationIds,
      pathogenIds: filters.report.pathogenIds,
    }
    // filters.reportSorting, // NOTE: we sort only on FE
    // {}
  );
  const {data: reportData, isLoading} = useTableData<any, HaiByPathogensData>(reportUrl, reportParams, 'content', haveDefaultFilters);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const hasFilters = !isEqual(
    normalizeHaiByPathogensFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeHaiByPathogensFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const hasHaisByPathogenSorting = !isEqual(omitNullsy(filters.haisByPathogenSort), omitNullsy(DEFAULT_HAIS_BY_PATHOGEN_SORTING));
  const hasHaisSorting = !isEqual(omitNullsy(filters.haisSort), omitNullsy(DEFAULT_HAIS_SORTING));
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const haiReportsStateOptions = enumMap.HaiReportsState ?? [];
  // scroll
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  // display
  const agensData = useAgens(config);
  const sortedAgensData = useMemo(() => sortBy(agensData ?? [], (v) => v.label), [agensData]);
  const haiTypeOptions = useMemo(
    () =>
      sortBy(
        enumMap.HaiTypeEnum.filter((v) => v.name !== 'HAI')
          .filter((v) => !v.isDeprecated)
          .map((v) => ({
            id: v.name,
            label:
              v.name === 'BSI'
                ? t(v.messageKey)
                    .replace(/\s*\([^)]*\)/g, '')
                    .trim()
                : t(v.messageKey),
          })),
        [(v) => v.label]
      ),
    [enumMap.HaiTypeEnum, t]
  );
  const selectedFiltersPane = (
    <LightSelectedFiltersPane
      selectedFilters={[
        {
          name: ['dateFrom', 'dateTo', 'floatingShortcutName'],
          label: t('reports.filters.date'),
          value: `${formatDate(filters.report.dateFrom)} - ${formatDate(filters.report.dateTo)}`,
          defaultValue: [defaultReportFilters.dateFrom, defaultReportFilters.dateTo, defaultReportFilters.floatingShortcutName],
        },
        {
          name: 'haiState',
          label: t('reports.filters.haiState'),
          value: tEnum('HaiReportsState', filters.report.haiState),
          defaultValue: defaultReportFilters.haiState,
        },
        {
          name: 'haiTypes',
          label: t('filter.hai.type'),
          value: (filters.report.haiTypes ?? []).map((v) => tEnum('HaiTypeEnum', v)).join(', '),
        },
        {
          name: 'organizationIds',
          label: t('filter.hai.department'),
          value: (filters.report.organizationIds ?? [])
            .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
            .join(', '),
          defaultValue: defaultReportFilters.organizationIds,
        },
        {
          name: 'pathogenIds',
          label: t('reports.filters.pathogenIds'),
          value: (filters.report.pathogenIds ?? []).map((id) => agensData.find((w) => String(w.id) === String(id))?.label).join(', '),
        },
      ]}
      tableFilters={filters.report}
      changeTableFilters={(newTableFilters) =>
        changeFilters({
          report: newTableFilters as PathogensReportFilters,
          haisPaging: {
            ...filters.haisPaging,
            page: 0,
          },
        })
      }
      formatters={formatters}
    />
  );
  return (
    <div
      css={muiStyles.contentWrapper}
      ref={scrollWrapperRef}
      onScroll={(event) => {
        setDefaultScroll((event.target as HTMLElement).scrollTop);
      }}
    >
      <Helmet title={`${t('reports.list.pageTitle')} | ${t(pageTitle)}`} />
      <div css={muiStyles.splitContentBox}>
        <LightFilterPanel
          open={filters.reportOpen}
          toggleOpen={() =>
            changeFilters({
              reportOpen: !filters.reportOpen,
            })
          }
          hasActiveFilter={hasFilters}
          maxScrollAreaHeight='40vh'
          savedFiltersProps={{
            storageIdPrefix,
            component,
            userFiltersLoading,
            addedUserFilters,
            userFilters,
            changeUserFilter,
            filter: filters.report,
            setFilter: (newFilter) => {
              changeFilters({
                report: newFilter as PathogensReportFilters,
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            },
            config,
            normalizeForCompare: normalizeHaiByPathogensFiltersForCompare,
          }}
          selectedFilters={selectedFiltersPane}
        >
          <LightMonthRangeFilter
            config={{
              name: 'date',
              ui: {label: 'reports.filters.date'},
            }}
            value={{
              startDate: filters.report.dateFrom,
              endDate: filters.report.dateTo,
              shortcut: filters.report.floatingShortcutName,
            }}
            defaultValue={
              defaultReportFilters.dateFrom && defaultReportFilters.dateTo
                ? {
                    startDate: defaultReportFilters.dateFrom,
                    endDate: defaultReportFilters.dateTo,
                    shortcut: defaultReportFilters.floatingShortcutName,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  dateFrom: newValue?.startDate,
                  dateTo: newValue?.endDate,
                  floatingShortcutName: newValue?.shortcut,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            showShortcuts
          />
          <LightSelectFilter
            config={{
              name: 'haiState',
              ui: {label: 'reports.filters.haiState'},
            }}
            options={haiReportsStateOptions}
            value={filters.report.haiState}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiState: newValue ?? defaultReportFilters.haiState,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
            isClearable={false}
          />
          <LightSelectFilter
            config={{
              name: `types`,
              ui: {label: 'filter.hai.type'},
            }}
            options={haiTypeOptions}
            multi
            value={filters.report.haiTypes}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  haiTypes: newValue,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
          />
          <LightOrganizationFilter2
            appConfig={config}
            config={{
              name: 'organizationIds',
              ui: {label: 'filter.hai.department'},
            }}
            multi
            isClearable
            value={filters.report.organizationIds}
            onChange={(newOrganizationIds) => {
              changeFilters({
                report: {
                  ...filters.report,
                  organizationIds: newOrganizationIds ?? null,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: `pathogenIds`,
              ui: {label: 'reports.filters.pathogenIds'},
            }}
            options={sortedAgensData ?? []}
            multi
            value={filters.report.pathogenIds}
            onChange={(newValue) => {
              changeFilters({
                report: {
                  ...filters.report,
                  pathogenIds: newValue,
                },
                haisPaging: {
                  ...filters.haisPaging,
                  page: 0,
                },
              });
            }}
          />
        </LightFilterPanel>
      </div>
      <HaiByPathogensReportTablesAndChart
        config={config}
        muiStyles={muiStyles}
        storageIdPrefix={storageIdPrefix}
        filters={filters}
        changeFilters={changeFilters}
        hasFilters={hasFilters}
        handleResetFilters={() =>
          changeFilters({
            report: defaultReportFilters,
            haisPaging: {
              ...filters.haisPaging,
              page: 0,
            },
          })
        }
        hasHaisByPathogenSorting={hasHaisByPathogenSorting}
        hasHaisSorting={hasHaisSorting}
        handleResetTopSorting={() =>
          changeFilters({
            haisByPathogenSort: DEFAULT_HAIS_BY_PATHOGEN_SORTING,
          })
        }
        handleResetHaisSorting={() =>
          changeFilters({
            haisSort: DEFAULT_HAIS_SORTING,
          })
        }
        formatters={formatters}
        isLoading={isLoading}
        reportData={reportData}
      />
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: scroll;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    flex: 0 0 auto;
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
    color: var(--dlTheme-700);
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  horizontalSplit: css`
    display: flex;
    align-items: flex-start;
    gap: 32px;
  `,
});
